import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch, AppState } from "../../redux/store";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import ToastComponent from "../../components/toast";
import PermissionGate from "../../core/layouts/permission-gate";
import paths from "../../navigation/paths";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import {
  getAllCandidatesState,
  allCandidatesStateAction,
  downloadCandidateCVStateAction,
  CandidateResponse,
  CandidatestatusIcons,
  getDownloadAllCVsState,
  downloadAllCVsStateAction,
} from "../../redux/features/candidate-slice";
import { UserRole } from "../../redux/features/user-slice";
import Button from "../../components/button";
import { format } from "date-fns/format";
import * as XLSX from "xlsx-js-style";
export default function CandidatesPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allCandidatesState = useSelector(getAllCandidatesState);
  const errorState = useSelector(getErrorState);
  const downloadCVState = useSelector(
    (state: AppState) => state.candidates.downloadCandidateCVState
  );
  const downloadAllCVsState = useSelector(getDownloadAllCVsState);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState<string>('applyDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const formatSalary = (salary: number | undefined): string => {
    return salary
      ? Number(salary).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : "--";
  };
  const fetchAllCandidates = useCallback(async () => {
    await dispatch(allCandidatesStateAction({}));
  }, [dispatch]);
  useEffect(() => {
    fetchAllCandidates();
  }, [fetchAllCandidates]);
  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "candidates", path: null, title: "Candidates" },
      ])
    );
  }, [dispatch]);
  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
      } else {
        toast(errorState.message, { type: "error" });
      }
    }
  }, [errorState, navigate]);
  // Handle CV download
  const handleDownloadCV = useCallback(
    async (candidateId: string, fileName: string) => {
      try {
        const result = await dispatch(
          downloadCandidateCVStateAction({ id: candidateId })
        ).unwrap();
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "cv.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast("Failed to download CV. Please try again.", { type: "error" });
      }
    },
    [dispatch]
  );
  const handleViewCV = useCallback(
    async (candidateId: string) => {
      try {
        const result = await dispatch(
          downloadCandidateCVStateAction({ id: candidateId })
        ).unwrap();
        const url = window.URL.createObjectURL(result);
        window.open(url, "_blank"); // Opens in new tab
        // Optional: revoke after a delay to free memory
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (error) {
        toast("Failed to view CV. Please try again.", { type: "error" });
      }
    },
    [dispatch]
  );

  const filteredCandidates = allCandidatesState.data
    .filter((candidate) => {
      const matchesName = candidate.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesEmail = candidate.email
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesPhone = candidate.phone
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus = candidate.status
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesJDName = candidate.jd.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const applyDate = candidate.applyDate ? new Date(candidate.applyDate) : null;
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      let dateMatch = true;
      if (from && applyDate) {
        dateMatch = dateMatch && applyDate >= from;
      }
      if (to && applyDate) {
        const toEnd = new Date(to);
        toEnd.setHours(23, 59, 59, 999);
        dateMatch = dateMatch && applyDate <= toEnd;
      }
      if (!applyDate && (from || to)) {
        dateMatch = false;
      }

      return (
        (matchesName ||
        matchesEmail ||
        matchesPhone ||
        matchesStatus ||
        matchesJDName) && dateMatch
      );
    })
    .sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      switch (sortKey) {
        case 'name': valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); break;
        case 'email': valA = a.email.toLowerCase(); valB = b.email.toLowerCase(); break;
        case 'phone': valA = (a.phone ?? '').toLowerCase(); valB = (b.phone ?? '').toLowerCase(); break;
        case 'status': valA = a.status.toLowerCase(); valB = b.status.toLowerCase(); break;
        case 'jdName': valA = a.jd.title.toLowerCase(); valB = b.jd.title.toLowerCase(); break;
        case 'englishLevel': valA = a.englishLevel.toLowerCase(); valB = b.englishLevel.toLowerCase(); break;
        case 'applyDate':
        default:
          valA = a.applyDate ? new Date(a.applyDate).getTime() : 0;
          valB = b.applyDate ? new Date(b.applyDate).getTime() : 0;
          break;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortIcon = (key: string) =>
    sortKey !== key ? ' ↕' : sortDirection === 'asc' ? ' ↑' : ' ↓';

  // Handle Excel export with styling
  const handleExportToExcel = useCallback(() => {
    const exportData = filteredCandidates.map((candidate: CandidateResponse) => ({
      Name: candidate.name,
      Email: candidate.email,
      Phone: candidate.phone || "--",
      SocialLink: candidate.socialLink || "--",
      Channel: candidate.channel,
      Status: candidate.status,
      "Current gross salary": formatSalary(candidate.currentSalary),
      "Expected gross salary": formatSalary(candidate.expectedSalary),
      "JD Name": candidate.jd.title,
      "Why Myteam choose you": candidate.message || "--",
      Certificate: candidate.certificate || "--",
      EnglishLevel: candidate.englishLevel,
      ApplyDate: candidate.applyDate
        ? format(candidate.applyDate, "yyyy-MM-dd")
        : "--",
      Link: `https://portal.myteamsolution.com.vn/candidates/${candidate.id}`,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    // Style the header row
    const headerCells = [
      "A1",
      "B1",
      "C1",
      "D1",
      "E1",
      "F1",
      "G1",
      "H1",
      "I1",
      "J1",
      "K1",
      "L1",
      "M1",
      "N1",
    ];
    headerCells.forEach((cell) => {
      worksheet[cell].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { horizontal: "center", vertical: "top", wrapText: true },
      };
    });
    // Apply text wrapping and top alignment to all cells
    const range = XLSX.utils.decode_range(worksheet["!ref"]!);
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          ...(worksheet[cellAddress].s || {}),
          alignment: {
            horizontal: "left",
            vertical: "top",
            wrapText: true,
          },
        };
      }
    }
    // Set column widths
    worksheet["!cols"] = [
      { wch: 20 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 30 }, // SocialLink
      { wch: 15 }, // Channel
      { wch: 15 }, // Status
      { wch: 15 }, // CurrentSalary
      { wch: 15 }, // ExpectedSalary
      { wch: 25 }, // JD Name
      { wch: 30 }, // Why Myteam choose you
      { wch: 20 }, // Certificate
      { wch: 15 }, // EnglishLevel
      { wch: 15 }, // ApplyDate
      { wch: 40 }, // Link
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    // Write the file with proper WritingOptions
    XLSX.writeFile(workbook, "candidates.xlsx", {
      bookType: "xlsx",
      type: "binary",
    });
  }, [filteredCandidates]);
  
  const handleDownloadAllCVs = useCallback(async () => {
    try {
      // Get IDs of filtered candidates
      const candidateIds = filteredCandidates.map((candidate) => candidate.id);
      if (candidateIds.length === 0) {
        toast("No candidates match the search criteria.", { type: "warning" });
        return;
      }
      const result = await dispatch(
        downloadAllCVsStateAction({ candidateIds })
      ).unwrap();
      const url = window.URL.createObjectURL(result);
      const link = document.createElement("a");
      link.href = url;
      link.download = "filtered-candidates-cvs.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast("Filtered CVs downloaded successfully!", { type: "success" });
    } catch (error) {
      toast("Failed to download filtered CVs.", { type: "error" });
    }
  }, [dispatch, filteredCandidates]);
  return (
    <PermissionGate
      categories={[]}
      roles={[UserRole.Admin, UserRole.Contributor]}
    >
      <Helmet title="All Candidates" />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <Button
            label="Add candidate"
            onClick={() => navigate(paths.ADD_CANDIDATE)}
            variant="filled"
          />
          <Button
            label="Export to Excel"
            onClick={handleExportToExcel}
            variant="outlined"
          />
          <Button
            label={
              downloadAllCVsState.loading
                ? "Downloading..."
                : "Download all cvs"
            }
            onClick={handleDownloadAllCVs}
            variant="outlined"
            disabled={downloadAllCVsState.loading}
          />
          <div className="flex items-center space-x-2">
            <label>From:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="p-2 border rounded"
            />
            <label>To:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="p-2 border rounded"
            />
          </div>
        </div>
        <input
          type="text"
          placeholder="Search by name, email, phone, status, or JD name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border rounded w-1/4"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          { id: "name", label: `Name${sortIcon('name')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('name') },
          { id: "email", label: `Email${sortIcon('email')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('email') },
          { id: "phone", label: `Phone${sortIcon('phone')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('phone') },
          { id: "status", label: `Status${sortIcon('status')}`, align: "text-left", width: "w-1/12", onClick: () => handleSort('status') },
          { id: "jdName", label: `JD Name${sortIcon('jdName')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('jdName') },
          { id: "englishLevel", label: `English Level${sortIcon('englishLevel')}`, align: "text-left", width: "w-1/12", onClick: () => handleSort('englishLevel') },
          { id: "applyDate", label: `Apply Date${sortIcon('applyDate')}`, align: "text-left", width: "w-1/12", onClick: () => handleSort('applyDate') },
          { id: "cvLink", label: "CV", align: "text-left", width: "w-1/12" },
        ]}
        body={
          allCandidatesState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={8} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : filteredCandidates.length > 0
            ? filteredCandidates.map((candidate: CandidateResponse) => (
                <tr
                  key={candidate.id}
                  className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                  onClick={() =>
                    navigate(`${paths.CANDIDATES}/${candidate.id}`)
                  }
                >
                  <TableBodyCell>
                    <label className="whitespace-normal break-words hover:underline cursor-pointer">
                      {candidate.name}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {candidate.email}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {candidate.phone}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words capitalize">
                      {CandidatestatusIcons[candidate.status]}{" "}
                      {candidate.status}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {candidate.jd.title}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words capitalize">
                      {candidate.englishLevel}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {candidate.applyDate
                        ? format(candidate.applyDate, "yyyy-MM-dd")
                        : "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    {candidate.cVLink ? (
                      <div className="flex gap-2">
                        <button
                          className="text-blue-600 hover:underline disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadCV(
                              candidate.id,
                              candidate.cVLink?.split("/").pop() || "cv.pdf"
                            );
                          }}
                          disabled={downloadCVState.loading}
                        >
                          {downloadCVState.loading
                            ? "Downloading..."
                            : "Download"}
                        </button>
                        <button
                          className="text-green-600 hover:underline disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCV(candidate.id);
                          }}
                          disabled={downloadCVState.loading}
                        >
                          {downloadCVState.loading ? "Loading..." : "View"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-500">--</span>
                    )}
                  </TableBodyCell>
                </tr>
              ))
            : [
                <tr key="empty-row">
                  <td colSpan={8} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Candidates found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allCandidates" />
    </PermissionGate>
  );
}